import { useMemo, useState } from 'react';
import type { TextureProvider } from '@/core/texture/provider';
import {
  listProviders,
  loadImageEndpointConfig,
  saveImageEndpointConfig,
} from '@/core/texture/provider';
import { EXAMPLE_PROMPTS } from '@/core/texture/prompt';
import { useDesignStore } from '@/state/store';
import { Section, Select } from '../components/Controls';

/**
 * Prompt-driven generation.
 *
 * The built-in provider runs offline and returns a parametric layer stack
 * rather than a picture, so the result arrives with all its sliders intact and
 * can be pushed further by hand. A second provider can call a real image model,
 * but the endpoint is supplied by whoever runs the app — no vendor is baked in.
 */
export function PromptTab() {
  const applyGenerated = useDesignStore((state) => state.applyGenerated);
  const notices = useDesignStore((state) => state.notices);

  const providers = useMemo(() => listProviders(), []);
  const [providerId, setProviderId] = useState(providers[0]?.id ?? 'prompt-synth');
  const [prompt, setPrompt] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showEndpoint, setShowEndpoint] = useState(false);

  const provider: TextureProvider | undefined = providers.find((entry) => entry.id === providerId);
  const ready = provider?.isReady() ?? false;

  async function generate(text: string, reroll = false): Promise<void> {
    const trimmed = text.trim();
    if (!trimmed || !provider) return;

    setBusy(true);
    setError(null);
    try {
      const result = await provider.generate({
        prompt: trimmed,
        // A different seed on "try again" gives a genuine alternative rather
        // than the same deterministic answer.
        seed: reroll ? Math.floor(Math.random() * 0xffff) : undefined,
      });
      applyGenerated(result, {
        kind: 'ai',
        label: result.label,
        prompt: trimmed,
        layers: result.layers,
      });
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Generation failed.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="prompt-tab">
      <form
        className="prompt-form"
        onSubmit={(event) => {
          event.preventDefault();
          void generate(prompt);
        }}
      >
        <textarea
          className="prompt-input"
          rows={3}
          value={prompt}
          placeholder="Describe a surface — “deep ocean waves spanning 3 panels”"
          onChange={(event) => setPrompt(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
              event.preventDefault();
              void generate(prompt);
            }
          }}
        />
        <div className="prompt-actions">
          <button
            type="submit"
            className="button button--primary"
            disabled={busy || !prompt.trim() || !ready}
          >
            {busy ? 'Generating…' : 'Generate'}
          </button>
          <button
            type="button"
            className="button button--ghost"
            disabled={busy || !prompt.trim() || !ready}
            onClick={() => void generate(prompt, true)}
            title="Generate another variation from the same description"
          >
            Try again
          </button>
        </div>
      </form>

      {!ready && provider?.readinessHint ? (
        <p className="status status--muted">{provider.readinessHint}</p>
      ) : null}
      {error ? <p className="status status--error">{error}</p> : null}

      {notices.length > 0 ? (
        <div className="notes">
          <h4 className="notes__title">What was generated</h4>
          <ul>
            {notices.map((note) => (
              <li key={note}>{note}</li>
            ))}
          </ul>
        </div>
      ) : null}

      <Section
        title="Try one of these"
        description="Each example uses a different part of the vocabulary."
      >
        <div className="chips chips--wrap">
          {EXAMPLE_PROMPTS.map((example) => (
            <button
              key={example}
              type="button"
              className="chip"
              disabled={busy}
              onClick={() => {
                setPrompt(example);
                void generate(example);
              }}
            >
              {example}
            </button>
          ))}
        </div>
      </Section>

      <Section
        title="Provider"
        description="The built-in synthesiser runs in your browser and sends nothing anywhere."
      >
        <Select
          label="Generator"
          value={providerId}
          options={providers.map((entry) => ({ value: entry.id, label: entry.name }))}
          onChange={setProviderId}
          help={provider?.description}
        />
        <button
          type="button"
          className="button button--ghost"
          onClick={() => setShowEndpoint((open) => !open)}
        >
          {showEndpoint ? 'Hide' : 'Configure'} image endpoint
        </button>
        {showEndpoint ? <EndpointSettings /> : null}
      </Section>
    </div>
  );
}

/**
 * Settings for the optional image-generation endpoint.
 *
 * Stored in this browser only. The prompt and the credentials go straight to
 * whatever URL is entered here and nowhere else.
 */
function EndpointSettings() {
  const stored = loadImageEndpointConfig();
  const [endpoint, setEndpoint] = useState(stored?.endpoint ?? '');
  const [token, setToken] = useState(stored?.token ?? '');
  const [saved, setSaved] = useState(false);

  return (
    <div className="endpoint">
      <label className="field__label" htmlFor="endpoint-url">
        Endpoint URL
      </label>
      <input
        id="endpoint-url"
        className="text-input"
        type="url"
        value={endpoint}
        placeholder="https://example.com/generate"
        onChange={(event) => {
          setEndpoint(event.target.value);
          setSaved(false);
        }}
      />

      <label className="field__label" htmlFor="endpoint-token">
        API token (optional)
      </label>
      <input
        id="endpoint-token"
        className="text-input"
        type="password"
        value={token}
        autoComplete="off"
        onChange={(event) => {
          setToken(event.target.value);
          setSaved(false);
        }}
      />

      <p className="field__help">
        Organic3D posts <code>{'{ "prompt": "…" }'}</code> and expects an image back. The URL and
        token are kept in this browser only.
      </p>

      <div className="prompt-actions">
        <button
          type="button"
          className="button"
          onClick={() => {
            saveImageEndpointConfig(
              endpoint.trim() ? { endpoint: endpoint.trim(), token: token || undefined } : null,
            );
            setSaved(true);
          }}
        >
          Save
        </button>
        {saved ? <span className="status status--muted">Saved.</span> : null}
      </div>
    </div>
  );
}
